import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * PostMaster — the honesty-stacked social-media manager.
 * PUBLIC draft rail: visitor drafts a post, PostMaster grades against
 * dark-pattern words, shouting, exclamation floods, and files a receipt.
 * Publish rails (X, LinkedIn, Bluesky, Mastodon) open when the Founder
 * seats credentials.
 */
export default function PostmasterPage() {
  const [receipts, setReceipts] = useState(null);
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("generic");
  const [tone, setTone] = useState("plain");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${BACKEND}/api/sage/bell/receipts`).then((r) => r.json()).then(setReceipts).catch(() => {});
  }, []);

  const refresh = () => fetch(`${BACKEND}/api/sage/bell/receipts`).then((r) => r.json()).then(setReceipts).catch(() => {});

  const submit = async (e) => {
    e.preventDefault();
    if (topic.trim().length < 3) { setErr("Draft too short. At least 3 characters."); return; }
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const r = await fetch(`${BACKEND}/api/sage/bell/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: topic.trim(), platform, tone }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setResult(d);
      refresh();
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black" data-testid="postmaster-page">
      <TopNav />
      <main className="max-w-3xl mx-auto px-6 py-16 space-y-10">
        <header className="space-y-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Live · public draft rail</div>
          <div className="flex items-start gap-4">
            <div className="text-postmaster-accent shrink-0"><ProductMark slug="postmaster" size={48} /></div>
            <h1 className="text-4xl sm:text-5xl font-normal tracking-tight" data-testid="postmaster-title">PostMaster</h1>
          </div>
          <p className="text-lg text-neutral-700 leading-relaxed">
            Draft a post. PostMaster reads it for dark-pattern words, shouting, and
            exclamation floods. If it's clean, you get a green mark. If it isn't,
            you get plain-English reasons why. Every draft files a receipt on the
            public counter below. The publish rails to X, LinkedIn, Bluesky, and
            Mastodon open when the Founder seats their keys.
          </p>
        </header>

        {/* Draft form */}
        <section className="border border-neutral-300 p-5" data-testid="postmaster-draft-form">
          <h2 className="text-lg font-medium mb-3">Draft a post</h2>
          <form onSubmit={submit} className="space-y-3">
            <textarea
              data-testid="postmaster-topic-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What are you trying to say? Write the post exactly as you would publish it."
              maxLength={400}
              rows={4}
              className="w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-black"
            />
            <div className="flex gap-3 flex-wrap">
              <label className="text-xs text-neutral-600 uppercase tracking-wide">
                Platform
                <select
                  data-testid="postmaster-platform-select"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="ml-2 border border-neutral-300 px-2 py-1 text-sm"
                >
                  <option value="generic">generic</option>
                  <option value="x">x</option>
                  <option value="bluesky">bluesky</option>
                  <option value="mastodon">mastodon</option>
                  <option value="linkedin">linkedin</option>
                </select>
              </label>
              <label className="text-xs text-neutral-600 uppercase tracking-wide">
                Tone
                <select
                  data-testid="postmaster-tone-select"
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="ml-2 border border-neutral-300 px-2 py-1 text-sm"
                >
                  <option value="plain">plain</option>
                  <option value="warm">warm</option>
                  <option value="pointed">pointed</option>
                </select>
              </label>
            </div>
            <button
              type="submit"
              disabled={busy || topic.trim().length < 3}
              data-testid="postmaster-draft-submit"
              className="border-2 border-black bg-black text-white px-5 py-2 text-xs uppercase tracking-wider font-bold hover:bg-white hover:text-black disabled:opacity-40 transition-colors"
            >
              {busy ? "Grading…" : "Draft and grade"}
            </button>
            {err && <div className="text-sm text-red-700" data-testid="postmaster-error">{err}</div>}
          </form>

          {result && (
            <div className="mt-5 border-t border-neutral-200 pt-4" data-testid="postmaster-result">
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs uppercase tracking-wider text-neutral-500">PostMaster says</div>
                <div
                  data-testid="postmaster-grade-badge"
                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${result.grade === "clean" ? "border-green-700 text-green-800" : "border-red-700 text-red-800"}`}
                >
                  {result.grade}
                </div>
              </div>
              <div className="text-sm text-neutral-800" data-testid="postmaster-verdict">{result.verdict}</div>
              {result.issues && result.issues.length > 0 && (
                <ul className="mt-2 text-xs text-neutral-700 list-disc pl-5">
                  {result.issues.map((i, idx) => <li key={idx}>{i}</li>)}
                </ul>
              )}
            </div>
          )}
        </section>

        {/* Live counters */}
        <section className="space-y-2" data-testid="postmaster-counters">
          <h2 className="text-lg font-medium">Live counters</h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="border border-neutral-200 px-4 py-3">
              <div className="text-2xl font-normal" data-testid="postmaster-drafts-filed">{receipts?.drafts_filed ?? "—"}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider mt-1">drafts filed</div>
            </div>
            <div className="border border-neutral-200 px-4 py-3">
              <div className="text-2xl font-normal">{receipts?.publishes_fired ?? "—"}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider mt-1">publishes fired</div>
            </div>
            <div className="border border-neutral-200 px-4 py-3">
              <div className="text-2xl font-normal">{receipts?.replies_triaged ?? "—"}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider mt-1">replies triaged</div>
            </div>
          </div>
          <div className="text-xs text-neutral-500 pt-1">
            Zeros are zeros. No mock rows. Publish + reply-triage rails open when the Founder seats real keys.
          </div>
        </section>
      </main>
    </div>
  );
}
